import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaKey, FaExclamationCircle, FaShieldAlt } from 'react-icons/fa';

import { Auth } from 'aws-amplify';
import LoaderButton from '../../../components/LoaderButton/LoaderButton';
import QRCode from 'qrcode.react';

import * as actions from '../../../store/actions/index';
import DisplayModeSwitch from '../../../theme/DisplayModeSwitch';
import { useI18n } from '../../../i18n';
import LanguageSwitch from '../../../i18n/LanguageSwitch';
import './Login.css';

const appEnv = process.env.REACT_APP_LOGIN_ENV || process.env.REACT_APP_GLOB_APP_ENV || 'default'
// SDCC crest asset is a wide wordmark with awkward whitespace; use the cleaner header tile instead
const crest = appEnv === 'sdcc'
	? require('../../../assets/images/sdcc_header.png')
	: require(process.env.REACT_APP_CREST)

const Login = (props) => {
	const { t } = useI18n();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [emailError, setEmailError] = useState('');
	const [passwordError, setPasswordError] = useState('');
	// confirmation modal
	const [showModal, setShowModal] = useState(false);
	const [confirmationEmail, setConfirmationEmail] = useState('');
	const [confirmationCode, setConfirmationCode] = useState('');
	const [confirmationError, setConfirmationError] = useState('');
	// reset password modal
	const [showResetModal, setShowResetModal] = useState(false);
	const [resetEmail, setResetEmail] = useState('');
	const [resetCode, setResetCode] = useState('');
	const [resetError, setResetError] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [mfaCode, setMfaCode] = useState('');

	useEffect(() => {
		if (!props.mfaRequired) {
			setMfaCode('');
		}
	}, [props.mfaRequired]);

	function validateEmail(email){
		if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email))
		  return true;
		else
		  return false;
	}

	const formReady = email.trim().length > 0 && password.length > 0;

	const validateConfirmationForm = () => {
		return confirmationEmail.length > 0
				&& validateEmail(confirmationEmail)
				&& confirmationCode.length > 0;
	}

	const validateResetForm = () => {
		return resetEmail.length > 0
				&& validateEmail(resetEmail)
				&& resetCode.length > 0
				&& newPassword.length > 0;
	}

	const handleCloseModal = () => {
		setShowModal(false);
		setConfirmationEmail('');
		setConfirmationCode('');
		setConfirmationError('');
	};

	const handleCloseResetModal = () => {
		setShowResetModal(false);
		setResetEmail('');
		setResetCode('');
		setNewPassword('');
		setResetError('');
	};

	const validateMfaForm = () => mfaCode.trim().length > 0;

	const validateTotpEnrollForm = () => /^\d{6}$/.test(mfaCode.replace(/\s/g, ''));

	const buildTotpUri = (secret, username) => {
		if (!secret || !username) return '';
		const issuerLabel = process.env.REACT_APP_MFA_ISSUER || 'Ipsum City';
		const issuer = encodeURIComponent(issuerLabel);
		const acct = encodeURIComponent(username);
		return `otpauth://totp/${issuer}:${acct}?secret=${secret}&issuer=${issuer}`;
	};

	const handleMfaSubmit = (event) => {
		event.preventDefault();
		props.onAuthCompleteMfa(mfaCode.trim());
	};

	const handleTotpEnrollSubmit = (event) => {
		event.preventDefault();
		props.onAuthCompleteTotpEnrollment(mfaCode.replace(/\s/g, ''));
	};

	const isTotpSetup = props.mfaRequired === 'MFA_SETUP' || props.mfaRequired === 'MFA_SETUP_OPTIONAL';
	const isOptionalTotpSetup = props.mfaRequired === 'MFA_SETUP_OPTIONAL';

	const handleFormSubmit = (event) => {
		if (isTotpSetup) {
			handleTotpEnrollSubmit(event);
		} else if (props.mfaRequired) {
			handleMfaSubmit(event);
		} else {
			handleSubmit(event);
		}
	};

	const handleCancelMfa = () => {
		setMfaCode('');
		props.onAuthCancelMfa();
	};

	const handleSkipOptionalMfa = () => {
		setMfaCode('');
		props.onAuthSkipOptionalMfa();
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		let nextEmailError = '';
		let nextPasswordError = '';
		if (!email.trim()) nextEmailError = t('login.emailRequired');
		else if (!validateEmail(email.trim())) nextEmailError = t('login.emailInvalid');
		if (!password) nextPasswordError = t('login.passwordRequired');
		setEmailError(nextEmailError);
		setPasswordError(nextPasswordError);
		if (nextEmailError || nextPasswordError) return;

		try {
			await props.onAuth(email.trim(), password);
			props.onSetAuthRedirectPath();
		}catch (e){
			console.log(e);
		}

	};

	const handleConfirmationSubmit = async (event) => {
		event.preventDefault();
		try {
			await Auth.confirmSignUp(confirmationEmail, confirmationCode);
			handleCloseModal();
		} catch (e) {
			setConfirmationError(e.message);
		}
	};

	const handleResetSubmit = async (event) => {
		event.preventDefault();
		try {
			await Auth.forgotPasswordSubmit(resetEmail, resetCode, newPassword);
			handleCloseResetModal();
		} catch (e) {
			setResetError(e.message);
		}
	};

	let errorMessage = null;
	if (props.error){
		errorMessage = (
			<div className="login-alert" role="alert" aria-live="polite">
				<FaExclamationCircle className="login-alert-icon" />
				<span>{props.error}</span>
			</div>);
	}

	let ConfirmErrorMsg = null;
	if (confirmationError){
		ConfirmErrorMsg = (
			<div className="login-alert" role="alert" aria-live="polite">
				<FaExclamationCircle className="login-alert-icon" />
				<span>{confirmationError}</span>
			</div>);
	}

	let ResetErrorMsg = null;
	if (resetError){
		ResetErrorMsg = (
			<div className="login-alert" role="alert" aria-live="polite">
				<FaExclamationCircle className="login-alert-icon" />
				<span>{resetError}</span>
			</div>);
	}

	const mfaHint =
		props.mfaRequired === 'MFA_SETUP_OPTIONAL'
			? t('login.mfaHintOptional')
			: props.mfaRequired === 'MFA_SETUP'
			? t('login.mfaHintSetup')
			: props.mfaRequired === 'SMS_MFA'
			? t('login.mfaHintSms')
			: t('login.mfaHintApp');

	let authRedirect = null;
	if (props.isAuthenticated) {
		const referer = (props.location.state && props.location.state.referer) || props.authRedirectPath;
		authRedirect = <Redirect to={referer}/>
	}

	return (
		<div className={`Login login-env-${appEnv}${props.mfaRequired ? ' login-mfa' : ''}`}>
			{authRedirect}
			<div className="login-card">
				{/* Branding panel */}
				<div className="login-brand">
					<div className="login-brand-crest">
						<img alt={t('login.crestAlt')} src={crest} />
					</div>
					<h2 className="login-brand-title">{t('common.productName')}</h2>
					<p className="login-brand-subtitle">{t('login.brandSubtitle')}</p>
				</div>

				{/* Form panel */}
				<div className="login-form-panel">
					{props.mfaRequired ? (
						<>
							<h1 className="login-title">
								{isTotpSetup ? t('login.mfaSetupTitle') : t('login.mfaVerifyTitle')}
							</h1>
							<p className="login-subtitle">{mfaHint}</p>
							<form onSubmit={handleFormSubmit}>
								{isTotpSetup && !props.mfaTotpSecret && (
									<p className="login-mfa-preparing">{t('login.mfaPreparing')}</p>
								)}
								{isTotpSetup && props.mfaTotpSecret && props.mfaEnrollUsername && (
									<div className="login-mfa-enroll">
										<div className="login-mfa-qr">
											<QRCode
												value={buildTotpUri(props.mfaTotpSecret, props.mfaEnrollUsername)}
												size={96}
												level="M"
												includeMargin={false}
											/>
										</div>
										<div className="login-mfa-manual">
											<p className="login-mfa-secret-hint">{t('login.mfaSecretHint')}</p>
											<code className="login-mfa-secret">{props.mfaTotpSecret}</code>
										</div>
									</div>
								)}
								<Form.Group controlId="mfaCode" className="login-group login-mfa-code-group">
									<Form.Label className="login-label">
										{isTotpSetup ? t('login.authenticatorCode') : t('login.verificationCode')}
									</Form.Label>
									<div className="login-input-wrap">
										<FaShieldAlt className="login-input-icon" />
										<Form.Control
											className="login-input"
											autoFocus={!isTotpSetup || !!props.mfaTotpSecret}
											type="text"
											inputMode="numeric"
											autoComplete="one-time-code"
											placeholder="123456"
											value={mfaCode}
											onChange={(e) => setMfaCode(e.target.value)} />
									</div>
								</Form.Group>

								{errorMessage}

								<LoaderButton
									block
									className={`login-submit${props.loading ? ' is-loading' : ''}`}
									disabled={
										isTotpSetup
											? !props.mfaTotpSecret || !validateTotpEnrollForm()
											: !validateMfaForm()
									}
									type="submit"
									isLoading={props.loading}
									text={isTotpSetup ? t('login.completeSetup') : t('login.verify')}
									loadingText={isTotpSetup ? t('login.saving') : t('login.verifying')}
								/>

								<div className={`login-links${!isOptionalTotpSetup ? ' login-links-single' : ''}`}>
									{isOptionalTotpSetup ? (
										<button
											type="button"
											className="login-link login-link-quiet"
											disabled={props.loading}
											onClick={handleSkipOptionalMfa}
										>
											{t('login.skipForNow')}
										</button>
									) : null}
									<button
										type="button"
										className="login-link"
										disabled={props.loading}
										onClick={handleCancelMfa}
									>
										{t('login.backToLogin')}
									</button>
								</div>
							</form>
						</>
					) : (
						<>
							<h1 className="login-title">{t('login.title')}</h1>
							<p className="login-subtitle">{t('login.subtitle')}</p>

							<form onSubmit={handleFormSubmit}>
								<Form.Group controlId="email" className={`login-group${emailError ? ' has-error' : ''}`}>
									<Form.Label className="login-label">{t('login.email')}</Form.Label>
									<div className="login-input-wrap">
										<FaEnvelope className="login-input-icon" />
										<Form.Control
											className="login-input"
											autoFocus
											type="email"
											placeholder={t('login.emailPlaceholder')}
											autoComplete="username"
											value={email}
											aria-invalid={emailError ? 'true' : 'false'}
											aria-describedby={emailError ? 'login-email-error' : undefined}
											onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
											onInput={(e) => setEmail(e.target.value)} />
									</div>
									{emailError ? <p id="login-email-error" className="login-field-error" role="alert">{emailError}</p> : null}
								</Form.Group>

								<Form.Group controlId="password" className={`login-group${passwordError ? ' has-error' : ''}`}>
									<Form.Label className="login-label">{t('login.password')}</Form.Label>
									<div className="login-input-wrap">
										<FaLock className="login-input-icon" />
										<Form.Control
											className="login-input login-input-password"
											type={showPassword ? 'text' : 'password'}
											placeholder={t('login.passwordPlaceholder')}
											autoComplete="current-password"
											value={password}
											aria-invalid={passwordError ? 'true' : 'false'}
											aria-describedby={passwordError ? 'login-password-error' : undefined}
											onChange={(e) => { setPassword(e.target.value); if (passwordError) setPasswordError(''); }}
											onInput={(e) => setPassword(e.target.value)} />
										<button
											type="button"
											className="login-password-toggle"
											aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
											aria-pressed={showPassword}
											onClick={() => setShowPassword(!showPassword)}>
											{showPassword ? <FaEyeSlash /> : <FaEye />}
										</button>
									</div>
									{passwordError ? <p id="login-password-error" className="login-field-error" role="alert">{passwordError}</p> : null}
								</Form.Group>

								{errorMessage}

								<LoaderButton
									block
									className={`login-submit${props.loading ? ' is-loading' : ''}`}
									disabled={!formReady}
									type="submit"
									isLoading={props.loading}
									text={t('login.submit')}
									loadingText={t('login.submitting')}
								/>

								<div className="login-links">
									<button type="button" className="login-link login-link-quiet" onClick={() => setShowModal(true)}>
										{t('login.confirmEmail')}
									</button>
									<button type="button" className="login-link" onClick={() => setShowResetModal(true)}>
										{t('login.forgotPassword')}
									</button>
								</div>
								<DisplayModeSwitch showWall={false} />
							</form>
						</>
					)}
					<LanguageSwitch variant="login" />
				</div>
			</div>

			{/* Confirm Email Modal */}
			<Modal centered className={`login-modal login-modal-env-${appEnv}`} show={showModal} onHide={handleCloseModal} animation={false} >
				<Modal.Header closeButton>
					<Modal.Title>
						<span className="login-modal-icon"><FaEnvelope /></span>
						{t('login.confirmEmailTitle')}
					</Modal.Title>
				</Modal.Header>
				<form onSubmit={handleConfirmationSubmit}>
					<Modal.Body>
						<p className="login-modal-hint">{t('login.confirmEmailHint')}</p>
						<Form.Group controlId="confirmationEmail" className="login-group">
							<Form.Label className="login-label">{t('login.email')}</Form.Label>
							<div className="login-input-wrap">
								<FaEnvelope className="login-input-icon" />
								<Form.Control className="login-input" autoFocus type="email" placeholder={t('login.emailPlaceholder')} value={confirmationEmail} onChange={(e) => setConfirmationEmail(e.target.value)} />
							</div>
						</Form.Group>
						<Form.Group controlId="confirmationCode" className="login-group">
							<Form.Label className="login-label">{t('login.confirmationCode')}</Form.Label>
							<div className="login-input-wrap">
								<FaKey className="login-input-icon" />
								<Form.Control className="login-input" type="text" placeholder={t('login.enterCode')} value={confirmationCode} onChange={(e) => setConfirmationCode(e.target.value)} />
							</div>
							<Form.Text id="chkEmailConfCode" muted>{t('login.checkEmailCode')}</Form.Text>
						</Form.Group>
						{ConfirmErrorMsg}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="light" className="login-modal-cancel" onClick={handleCloseModal}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" className="login-modal-submit" disabled={!validateConfirmationForm()}>
							{t('login.confirm')}
						</Button>
					</Modal.Footer>
				</form>
			</Modal>

			{/* Forgot Password Modal */}
			<Modal centered className={`login-modal login-modal-env-${appEnv}`} show={showResetModal} onHide={handleCloseResetModal} animation={false} >
				<Modal.Header closeButton>
					<Modal.Title>
						<span className="login-modal-icon"><FaLock /></span>
						{t('login.resetPasswordTitle')}
					</Modal.Title>
				</Modal.Header>
				<form onSubmit={handleResetSubmit}>
					<Modal.Body>
						<p className="login-modal-hint">{t('login.resetPasswordHint')}</p>
						<Form.Group controlId="resetEmail" className="login-group">
							<Form.Label className="login-label">{t('login.email')}</Form.Label>
							<div className="login-input-wrap">
								<FaEnvelope className="login-input-icon" />
								<Form.Control className="login-input" autoFocus type="email" placeholder={t('login.emailPlaceholder')} value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} />
							</div>
						</Form.Group>
						<Form.Group controlId="resetCode" className="login-group">
							<Form.Label className="login-label">{t('login.resetCode')}</Form.Label>
							<div className="login-input-wrap">
								<FaKey className="login-input-icon" />
								<Form.Control className="login-input" type="text" placeholder={t('login.enterCode')} value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
							</div>
							<Form.Text id="chkEmailCode" muted>{t('login.askAdminCode')}</Form.Text>
						</Form.Group>
						<Form.Group controlId="newPassword" className="login-group">
							<Form.Label className="login-label">{t('login.newPassword')}</Form.Label>
							<div className="login-input-wrap">
								<FaLock className="login-input-icon" />
								<Form.Control className="login-input" type="password" placeholder={t('login.newPasswordPlaceholder')} autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
							</div>
						</Form.Group>
						{ResetErrorMsg}
					</Modal.Body>
					<Modal.Footer>
						<Button variant="light" className="login-modal-cancel" onClick={handleCloseResetModal}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" className="login-modal-submit" disabled={!validateResetForm()}>
							{t('login.resetPassword')}
						</Button>
					</Modal.Footer>
				</form>
			</Modal>
		</div>

	);
}

const mapStateToProps = state => {
    return {
        loading: state.auth.loading,
        error: state.auth.error,
        isAuthenticated: state.auth.token !== null,
		authRedirectPath: state.auth.authRedirectPath,
		userId: state.auth.userId,
		mfaRequired: state.auth.mfaRequired,
		mfaTotpSecret: state.auth.mfaTotpSecret,
		mfaEnrollUsername: state.auth.mfaEnrollUsername
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onAuth: ( email, password ) => dispatch( actions.auth( email, password ) ),
        onAuthCompleteMfa: (code) => dispatch(actions.authCompleteMfa(code)),
        onAuthCompleteTotpEnrollment: (code) => dispatch(actions.authCompleteTotpEnrollment(code)),
        onAuthCancelMfa: () => dispatch(actions.authCancelMfa()),
        onAuthSkipOptionalMfa: () => dispatch(actions.authSkipOptionalMfa()),
        onSetAuthRedirectPath: () => dispatch(actions.setAuthRedirectPath('/'))
    };
};

export default connect( mapStateToProps, mapDispatchToProps )( Login );
